import { useCallback } from "react";

import { swSettings } from "app/common/services/swSettings";
import { SharingAssetModal } from "app/components/sharingAssetModal/SharingAssetModal";
import type { ISharing } from "app/components/sharingAssetModal/types";
import { ESharingState } from "app/components/sharingAssetModal/types";
import type { ICustomSegment } from "app/services/segments/segmentsApiService";
import { ESegmentShareState, ESegmentShareStateToServer } from "app/services/segments/segmentsApiService";
import { allTrackers } from "app/services/track/track";

import { useBulkShareSegments } from "../hooks/useBulkShareSegments";
import { useBulkUnshareSegments } from "../hooks/useBulkUnshareSegments";
import { getBulkModalText } from "../sharingModalCopy";

interface IBulkShareSegmentsModalProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    selectedSegments: ICustomSegment[];
    onShareSuccess?: () => void;
    onUnshareSuccess?: () => void;
    onError?: () => void;
}

const getInitialSharingState = (segments: ICustomSegment[]): ESharingState => {
    const allPrivate = segments.every((segment) => segment.targetType === ESegmentShareState.PRIVATE);
    return allPrivate ? ESharingState.PRIVATE : ESharingState.SUBSET_OF_USERS;
};

export const BulkShareSegmentsModal = ({
    isOpen,
    setIsOpen,
    selectedSegments,
    onShareSuccess,
    onUnshareSuccess,
    onError,
}: IBulkShareSegmentsModalProps) => {
    const segmentCount = selectedSegments.length;
    const modalText = getBulkModalText(segmentCount);

    const { mutateAsync: shareSegmentsBatch } = useBulkShareSegments({
        onSuccess: () => {
            onShareSuccess?.();
        },
        onError: () => {
            onError?.();
        },
    });

    const { mutateAsync: unshareSegmentsBatch } = useBulkUnshareSegments({
        onSuccess: () => {
            onUnshareSuccess?.();
        },
        onError: () => {
            onError?.();
        },
    });

    const handleSubmit = useCallback(
        async (sharing: ISharing): Promise<void> => {
            const { sharingState, sharedWithUsers, notification } = sharing;

            const segmentIds = selectedSegments.map((segment) => segment.id);

            allTrackers.dispatchTrackEvent("bulk share segments", "submit", "share segments", {
                segmentCount,
                sharingState,
                sharedWithUsersCount: sharingState === ESharingState.SUBSET_OF_USERS ? sharedWithUsers.length : 0,
            });

            if (sharingState === ESharingState.PRIVATE) {
                await unshareSegmentsBatch({ SegmentIds: segmentIds });
                return;
            }

            const viewSharingType =
                sharingState === ESharingState.ACCOUNT
                    ? ESegmentShareStateToServer.PUBLIC
                    : ESegmentShareStateToServer.RESTRICTED;

            const accountIds = sharingState === ESharingState.ACCOUNT ? [swSettings.user.accountId as number] : [];

            const userIds = sharingState === ESharingState.SUBSET_OF_USERS ? sharedWithUsers : [];

            const params = {
                segmentIds,
                newUsersAdd: userIds,
                newUsersRemove: [],
                userIds,
                accountIds,
                viewSharingType,
                message:
                    notification.shouldNotify && sharingState === ESharingState.SUBSET_OF_USERS
                        ? notification.notificationContent
                        : undefined,
            };

            await shareSegmentsBatch(params);
        },
        [selectedSegments, segmentCount, shareSegmentsBatch, unshareSegmentsBatch],
    );

    if (segmentCount === 0) {
        return null;
    }

    return (
        <SharingAssetModal
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            modalText={modalText}
            onSubmit={handleSubmit}
            initialSharingState={getInitialSharingState(selectedSegments)}
        />
    );
};
